import React from "react";
import { Bvh, Plane, useCursor, useGLTF } from "@react-three/drei";
import { state } from "./store";
import { useSnapshot } from "valtio";
import * as THREE from "three/webgpu";
import { TransitionMaterial } from "./TransitionMaterial/TransitionMaterial";

export default function Scoutglb(props) {
  const { nodes, materials } = useGLTF("/scout2-transformed.glb");
  const { waterColor, hoveredModel, hullColor, accentColor, deckColor, seatsColor, bodyColor, isDraggingCamera, allowHover, allowClick, finishedIntro } = useSnapshot(state);

  // Define element information and their custom messages
  const elementInfo = {
    hull: {
      name: "Hull",
      message: "Change hull",
    },
    upholstery: {
      name: "Upholstery",
      message: "Change upholstery",
    },
    accent: {
      name: "roof003",
      message: "Change accent",
    },
    Deck: {
      name: "Deck",
      message: "Change deck",
    },
    body: {
      name: "Body",
      message: "Change body",
    },
  };

  useCursor(hoveredModel, "pointer");

  // Helper function to handle hover events
  const handleHover = (elementName) => {
    // Don't trigger hover events if interactions are disabled or intro not finished
    if (!allowHover || !finishedIntro) return;

    state.hoveredModel = true;
    state.hoveredElement = elementName;
    state.hoveredElementName = elementInfo[elementName]?.message || "Interact with element";
  };

  const handleHoverOut = () => {
    // Always clear hover state on hover out, regardless of allowHover state
    state.hoveredModel = false;
    state.hoveredElement = null;
    state.hoveredElementName = "";
  };

  // Helper function to handle click events
  const handleClick = (panelName) => {
    // Don't trigger click events if interactions are disabled or intro not finished
    if (!allowClick || !finishedIntro) return;

    state.hoveredModel = false;
    state.hoveredElement = null;
    state.hoveredElementName = "";
    state.openedPanel = panelName;
    state.hideInstruction = true;
  };

  // Helper function to clear hover state (can be called externally)
  const clearHoverState = () => {
    state.hoveredModel = false;
    state.hoveredElement = null;
    state.hoveredElementName = "";
  };

  // Enhanced click handler with multiple safety checks
  const handleClickSafe = (panelName, event) => {
    // Multiple safety checks to prevent accidental clicks
    if (!allowClick || !allowHover || isDraggingCamera || !finishedIntro) {
      // For Three.js events, we use stopPropagation() which is available
      if (event && typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
      return;
    }

    // Additional check: if we're in the middle of a drag operation
    if (state.isDraggingCamera) {
      if (event && typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
      return;
    }

    handleClick(panelName);
  };

  return (
    <group {...props} dispose={null}>
      <Bvh>
        <mesh castShadow receiveShadow geometry={nodes.Black_Plastic.geometry} material={materials.M_Plastic_Black} rotation={[Math.PI / 2, 0, 0]} scale={0.01} />
        <mesh castShadow receiveShadow geometry={nodes.Chrome.geometry} material={materials.M_Chrome} rotation={[Math.PI / 2, 0, 0]} scale={0.01} />
        <mesh
          geometry={nodes.upholstery.geometry}
          rotation={[Math.PI / 2, 0, 0]}
          onPointerOver={(e) => {
            e.stopPropagation();
            handleHover("upholstery");
          }}
          onPointerOut={(e) => {
            e.stopPropagation();
            handleHoverOut();
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleClickSafe("seats-colors", e);
          }}
        >
          <TransitionMaterial roughness={1} normalMap={materials.M_Fabric_Blue.normalMap} transitionColor={seatsColor || "#335688"} metalness={0} duration={2} center={[0.5, 0.5]} normalScale={[1, 15]} />
        </mesh>
        <mesh castShadow receiveShadow geometry={nodes.Everest.geometry} material={materials.M_Everest_Logo} rotation={[Math.PI / 2, 0, 0]} scale={0.01} />
        <mesh castShadow receiveShadow geometry={nodes.Frosted_Glass.geometry} material={materials["M_Clear.001"]} rotation={[Math.PI / 2, 0, 0]} scale={0.01} />
        <mesh castShadow receiveShadow geometry={nodes.GlassTrim.geometry} material={materials.M_Glass_Trim} material-metalness={0.2} material-roughness={0.1} rotation={[Math.PI / 2, 0, 0]} />
        <mesh castShadow receiveShadow geometry={nodes.HullWindow.geometry} material={materials.M_Glass_Tinted_Darker} rotation={[Math.PI / 2, 0, 0]} />
        <mesh castShadow receiveShadow geometry={nodes.Interior.geometry} material={materials.M_Interior} rotation={[Math.PI / 2, 0, 0]} />
        <mesh castShadow receiveShadow geometry={nodes.Metal_Black.geometry} material={materials.M_Metal_Black} rotation={[Math.PI / 2, 0, 0]} scale={0.01} />
        <mesh castShadow receiveShadow geometry={nodes.Metal_Satin.geometry} material={materials.M_Satin_Steel} rotation={[Math.PI / 2, 0, 0]} scale={0.01} />
        <mesh
          geometry={nodes.accent.geometry}
          rotation={[Math.PI / 2, 0, 0]}
          scale={0.01}
          onPointerOver={(e) => {
            e.stopPropagation();
            handleHover("accent");
          }}
          onPointerOut={(e) => {
            e.stopPropagation();
            handleHoverOut();
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleClickSafe("accent-colors", e);
          }}
        >
          <TransitionMaterial roughness={0.1} metalness={0.7} transitionColor={accentColor || "#454a4d"} duration={2} center={[0.5, 0.5]} />
        </mesh>
        <group rotation={[Math.PI / 2, 0, 0]} scale={0.01}>
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.main_1.geometry}
            onPointerOver={(e) => {
              e.stopPropagation();
              handleHover("body");
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              handleHoverOut();
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleClickSafe("body-colors", e);
            }}
          >
            <TransitionMaterial roughness={0.1} metalness={0.7} transitionColor={bodyColor || "#d9dde3"} duration={2} center={[0.5, 0.5]} />
          </mesh>
          <mesh castShadow receiveShadow geometry={nodes.main_2.geometry} material={materials.M_Chrome} />
          <mesh castShadow receiveShadow geometry={nodes.main_3.geometry}>
            <TransitionMaterial roughness={0.1} metalness={0.7} transitionColor={bodyColor || "#d9dde3"} duration={2} center={[0.5, 0.5]} />/
          </mesh>
        </group>
        <mesh castShadow receiveShadow geometry={nodes.Tabletop.geometry} material={materials.M_Wood_Varnished} rotation={[Math.PI / 2, 0, 0]} />
        <mesh
          geometry={nodes.deck.geometry}
          castShadow
          receiveShadow
          material={materials.M_Floor_Teak}
          material-roughness={0.85}
          material-normalScale={[-2.5, 2.5]}
          rotation={[Math.PI / 2, 0, 0]}
          material-color={deckColor === "#c9a96e" ? "#ffffff" : deckColor === "#9c8355" ? "#faded7" : deckColor || "#ffffff"}
          onPointerOver={(e) => {
            e.stopPropagation();
            handleHover("Deck");
          }}
          onPointerOut={(e) => {
            e.stopPropagation();
            handleHoverOut();
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleClickSafe("deck-colors", e);
          }}
        />
        <mesh castShadow receiveShadow renderOrder={1} geometry={nodes.Tinted_Glass.geometry} material={materials.M_Glass_Tinted} material-roughness={0.1} material-metalness={0.5} material-color={"#394245"} rotation={[Math.PI / 2, 0, 0]} />
        <mesh castShadow receiveShadow geometry={nodes.Unique_Parts.geometry} material={materials.M_Unique} rotation={[Math.PI / 2, 0, 0]} scale={0.01} />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.hull.geometry}
          rotation={[Math.PI / 2, 0, 0]}
          onPointerOver={(e) => {
            e.stopPropagation();
            handleHover("hull");
          }}
          onPointerOut={() => {
            handleHoverOut();
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleClickSafe("hull-colors", e);
          }}
        >
          <TransitionMaterial roughness={0.1} metalness={0.6} transitionColor={hullColor || "#454a4d"} duration={2} center={[0.5, 0.5]} />
        </mesh>
        <mesh castShadow receiveShadow geometry={nodes.Hull_Trim.geometry} material={materials["hull_trim.001"]} />
        <mesh castShadow receiveShadow geometry={nodes.Beachdoor_Trim_left.geometry} material={materials["hull_trim.001"]} />
      </Bvh>
    </group>
  );
}

useGLTF.preload("/scout2-transformed.glb");
